import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import './../styles/Main.css';
import './../styles/SupportPage.css';
import DocumentService from './../services/DocumentService';
import { useAuth } from '../context/AuthContext';
import getBaseUrl from './../utils/baseUrl';
import VideoModal from './modals/VideoModal';
import { useNavigate, useParams } from 'react-router-dom';

function SupportPage() {
  const [content, setContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const { documentName } = useParams();

  // Fetch documents when the component mounts
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const docs = await DocumentService.getDocuments('Support');
        setDocuments(docs);

        // Automatically select the document based on URL or default to the first document
        if (!documentName && docs.length > 0) {
          setSelectedDocument(docs[0]);
          loadHtmlContent(docs[0].fileName);
        } else if (documentName) {
          loadHtmlContent(documentName);
        }
      } catch (error) {
        console.error('Failed to fetch documents');
      }
    };

      fetchDocuments();
  }, [documentName]);

  useEffect(() => {
    if (documentName) {
      loadHtmlContent(documentName);
    }
  }, [documentName]);

  const loadHtmlContent = async (fileName) => {
    try {
      const response = await fetch(`${getBaseUrl()}/support/${fileName}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      let markdownText = await response.text();
      let htmlText = marked(markdownText);

      // Fix image paths to use absolute URLs
      const baseUrl = getBaseUrl();
      htmlText = htmlText.replace(/src="(?:\.\/)?images\//gi, `src="${baseUrl}/support/images/`);

      const sanitizedHtml = DOMPurify.sanitize(htmlText, { USE_PROFILES: { html: true } });
      setContent(sanitizedHtml);
      setTimeout(() => addLinkClickListener(), 100); // Add event listeners after rendering
    } catch (error) {
      console.error('Error loading the HTML file');
      setContent('<p>Error loading content. Please try again later.</p>');
    }
  };

  const addLinkClickListener = () => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.removeEventListener('click', handleLinkClick);
        link.addEventListener('click', handleLinkClick);
      });
    }
  };

  const handleLinkClick = (event) => {
    event.preventDefault();
    let href = event.currentTarget.getAttribute('href');

    if (href.startsWith('./') || href.startsWith('../')) {
      href = href.replace('./../', '/support/').replace('../', '/support/');
    }

    if (href && (href.endsWith('.mp4') || href.endsWith('.mov') || href.endsWith('.webm'))) {
      const videoPath = href.startsWith('/') ? href : `${getBaseUrl()}/${href}`;
      setVideoUrl(videoPath);
      setIsModalOpen(true);
    } else if (href.startsWith('/support/') || href.startsWith('support/')) {
      const documentName = href.split('/').pop();
      navigate(`/support/${documentName}`, { replace: true });
    } else {
      window.open(href, '_blank');
    }
  };

  const handleTypeSelect = (id) => {
    const selectedDoc = documents.find(doc => doc.id === id);
    if (selectedDoc) {
      setSelectedDocument(selectedDoc);
      navigate(`/support/${selectedDoc.fileName}`, { replace: true });
    }
  };

  // Helper function to escape special regex characters
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length > 2) {
      const escapedValue = escapeRegExp(value);
      const regex = new RegExp(`(${escapedValue})`, 'gi');
      const highlightedContent = content.replace(regex, '<span class="highlight">$1</span>');
      setContent(DOMPurify.sanitize(highlightedContent));

      setTimeout(() => {
        const firstHighlight = document.querySelector('.highlight');
        if (firstHighlight) {
          firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      loadHtmlContent(documentName);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (selectedDocument) {
      loadHtmlContent(selectedDocument.fileName);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setVideoUrl(null);
  };

  return (
    <div className="support-page-layout">
      <div className="windows-header">
        <p className="windows-title"></p>
        <div className="search-input-button-container">
          <div className="search-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button className="search-button" title="Clear" onClick={clearSearch}>Clear</button>
        </div>
      </div>
      <div className="support-header">
        <p className="support-title"></p>
        <div className="support-tabs-container">
          <div className="support-tabs">
            {documents.map(doc => (
              <div
                key={doc.id}
                className={`support-tab ${selectedDocument && selectedDocument.id === doc.id ? 'selected' : ''}`}
                onClick={() => handleTypeSelect(doc.id)}
              >
                {doc.title}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="support-content">
        {content ? (
          <div ref={contentRef} dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          <p>Loading content...</p>
        )}
      </div>
      {isModalOpen && (
        <VideoModal videoUrl={videoUrl} isOpen={isModalOpen} onClose={closeModal} />
      )}
    </div>
  );
}

export default SupportPage;
